import {v4 as uuid } from '../utils/uuid.js';
import {nextTick } from '../utils/nextTick.js';
import { ClassBase } from './ClassBase.js'
import {pubsub} from './pubsub.js'

/**
	@typedef {Object} Tab#Options {@link Tab}のオプションオブジェクト
	@prop {JQuery | HTMLElement | String} [btns=[data-tab-btn]] 切り替え用ボタン要素を指定
	@prop {JQuery | HTMLElement | String} [contents=[data-tab-content]] 切り替えコンテンツ要素を指定
	@prop {String} [defaultOpen] 初期化時に開くタブの指定。指定がない場合は1番目のタブ
	@prop {Tab#beforeChange} [beforeChange=null] 切り替え前に実行される関数
	@prop {Tab#afterChange} [afterChange=null] 切り替え後に実行される関数
*/

/**
	切り替え前に実行される関数
	@callback Tab#beforeChange
	@param {Tab} instance {@link Tab}のインスタンス
*/

/**
	切り替え後に実行される関数
	@callback Tab#afterChange
	@param {Tab} instance {@link Tab}のインスタンス
*/

/**
	切り替え前に発火するイベント
	@event Tab#tab:beforeChange
	@param {Tab} instance {@link Tab}のインスタンス
*/

/**
	切り替え後に発火するイベント
	@event Tab#tab:afterChange
	@param {Tab} instance {@link Tab}のインスタンス
*/



/**
@class Tab
@classdesc タブ
@param {JQuery | HTMLElement | String} el 対象のルート要素を指定
@param {Tab#Options} [op] オプションオブジェクトを指定

@example <caption>HTML:基本構造</caption>
<div class="example">
	<ul>
		<li><button class="example_btn" data-tab-btn="1">1</button></li>
		<li><button class="example_btn" data-tab-btn="2">2</button></li>
		<li><button class="example_btn" data-tab-btn="3">3</button></li>
		<li><button class="example_btn" data-tab-btn="4">4</button></li>
	</ul>
	<div class="example_content" data-tab-content="1">1</div>
	<div class="example_content" data-tab-content="2">2</div>
	<div class="example_content" data-tab-content="3">3</div>
	<div class="example_content" data-tab-content="4">4</div>
</div>
<!-- ※data-tab-btn、data-tab-contentを省略した場合はhtmlの順番に従って自動でIDが振られる -->

@example <caption>JS:オプションにコールバック関数渡して何かするパターン</caption>
import {Tab} from '/assets/js/modules/Tab.js';
const example = new Tab('.example', {
	btns: '.example_btn',
	contents: '.example_content',
	beforeChange(tab){
		console.log('beforeChange', {
			from: tab.prev,
			to: tab.current,
		});
	},
	afterChange(tab){
		console.log('afterChange', {
			from: tab.prev,
			to: tab.current,
		});
	},
})


@example <caption>JS:イベントの発火を拾って何かするパターン</caption>
import {Tab} from '/assets/js/modules/Tab.js';
const example = new Tab('.example', {
	btns: '.example_btn',
	contents: '.example_content',
})
$('.example').on('tab:beforeChange', function(event, tab) {
	event.preventDefault();
	console.log('tab:beforeChange', {
		from: tab.prev,
		to: tab.current,
	});
});
$('.example').on('tab:afterChange', function(event, tab) {
	event.preventDefault();
	console.log('tab:afterChange', {
		from: tab.prev,
		to: tab.current,
	});
});


@example <caption>JS:changeメソッドが返すPromiseで何かするパターン</caption>
import {Tab} from '/assets/js/modules/Tab.js';
const example = new Tab('.example', {
	btns: '.example_btn',
	contents: '.example_content',
	addEvent: false,
})
$(document).on('click', '.example_btn', function(event) {
	event.preventDefault();
	console.log('BeforeChange', {
		from: example.prev,
		to: example.current,
	});
	const _to = $(this).attr('data-tab-btn');
	example.change(_to).then(() => {
		console.log('AfterChange', {
			from: example.prev,
			to: example.current,
		});
	})
});


*/
export class Tab extends ClassBase {
	constructor(el, op) {
		super(el)
		this.op = {
			...this.op,
			btns: '[data-tab-btn]',
			contents: '[data-tab-content]',
			defaultOpen: null,
			beforeChange: null,
			afterChange: null,
			...op,
		}

		/**
			ルート要素
			@type {JQuery}
			@name Tab#$el
		*/


		/**
			タブ切り替えボタン要素
			@type {JQuery}
		*/
		this.$btns = this.$el.find(this.op.btns);

		/**
			タブ切り替えコンテンツ要素
			@type {JQuery}
		*/
		this.$contents = this.$el.find(this.op.contents);


		this.list = {};
		for (let i = 0, l = this.$contents.length; i < l; i++) {
			const _id = this.$contents.eq(i).attr('data-tab-content') || uuid();
			this.list[_id] = new TabItem(_id, this.$btns.eq(i), this.$contents.eq(i))
		}


		/**
			アクティブなタブのID
			@type {String}
		*/
		this.current = this.$el.attr('data-tab-root') || this.op.defaultOpen || this.$contents.eq(0).attr('data-tab-content')
		this.$el.attr('data-tab-root', this.current)
		this.list[this.current].snapOpen()

		/**
			1個前にアクティブだったタブのID
			@type {String}
		*/
		this.prev = this.current;


		this.#init()
	}

	#init(){
		if(this.op.addEvent) {
			this.$btns.on('click', (event) => {
				event.preventDefault();
				const _to = $(event.target).attr('data-tab-btn');
				this.change(_to)
			});
		}

		this.$btns.on({
			focus: (e) => {
				const _to = $(e.target).data('tab-btn')
				this.change(_to)
			},
			blur: () => {}
		})

		this.$contents.find('*').on({
			focus: (e) => {
				const _to = $(e.target).closest('[data-tab-content]').data('tab-content')
				this.change(_to)
			},
			blur: () => {}
		})
	}

	/**
		タブ切り替え
		@param {Any} to 切り替えるタブのID
	*/
	change(to) {
		return new Promise((resolve, reject) => {
			if(this.current !== to) {
				this.prev = this.current;
				this.current = to;

				this.op.beforeChange && this.op.beforeChange(this)
				this.$el.trigger('tab:beforeChange', this)
				pubsub.pub('tab:beforeChange', this);

				this.list[this.prev].close();
				this.$el.attr('data-tab-root', this.current)
				this.list[this.current].open().then(() => {
					this.op.afterChange && this.op.afterChange(this)
					this.$el.trigger('tab:afterChange', this)
					pubsub.pub('tab:afterChange', this);
					resolve(this)
				})
			}
		})
	}
}


class TabItem {
	constructor(id, btn, content){
		this.id = id

		this.$btn = btn;
		this.$btn.attr('data-tab-btn', this.id)

		this.$content = content;
		this.$content.attr('data-tab-content', this.id)
	}

	open(){
		return new Promise(resolve => {
			this.$content
				.off('transitionend')
				.on('transitionend', (e) => {
					if(e.originalEvent.target != this.$content[0]) return;
					windowInfo.updateSize();
					resolve(this)
				});
			this.$btn.attr('data-tab-btn-active', '');
			this.$content.attr('data-tab-content-active', '');
		})
	}

	snapOpen(){
		this.$btn.attr('data-tab-btn-active', '');
		this.$content
			.attr('data-tab-content-snap', '')
			.attr('data-tab-content-active', '');
		nextTick(() => {
			this.$content.removeAttr('data-tab-content-snap');
		})
	}

	close(){
		this.$btn.removeAttr('data-tab-btn-active');
		this.$content.removeAttr('data-tab-content-active');
	}
}


/**
$('[data-tab]')をターゲットに[data-tab]の値をオプションに{@link Tab}をインスタンス化する

@example <caption>HTML</caption>
<div class="example" data-tab='{
	"defaultOpen": 3
}'>
	<ul>
		<li><button class="example_btn" data-tab-btn="1">1</button></li>
		<li><button class="example_btn" data-tab-btn="2">2</button></li>
		<li><button class="example_btn" data-tab-btn="3">3</button></li>
		<li><button class="example_btn" data-tab-btn="4">4</button></li>
	</ul>
	<div class="example_content" data-tab-content="1">1</div>
	<div class="example_content" data-tab-content="2">2</div>
	<div class="example_content" data-tab-content="3">3</div>
	<div class="example_content" data-tab-content="4">4</div>
</div>


@example <caption>JS</caption>
import {tab} from '/assets/js/modules/Tab.js';
tab();
*/
export const tab = () => {
	$('[data-tab]').each(function(index, el) {
		new Tab(this, $(this).data('tab'))
	});
}